import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useT, Namespace, useApiErrorMessage } from "@racoon/i18n";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  useToast,
} from "@racoon/ui";
import { ConnectProto, type ApiError } from "@racoon/api";
import {
  useCreateContainerTypeMutation,
  useListContainerTypesQuery,
  useUpdateContainerTypeMutation,
} from "../../../../shared/api/container-type/container-type.grpc";
import { foreignOwnerName, isOwnType } from "~/shared/lib/holding-type";
import { useCanWriteType } from "~/shared/hooks/use-can-write-type";

type ContainerType = ConnectProto.ContainerType;

interface BunkerTypeFormData {
  name: string;
  volume: number;
  weight: number;
}

interface BunkerTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  containerType?: ContainerType | null;
  ownerOrganizationId?: number | null;
}

export const BunkerTypeDialog: React.FC<BunkerTypeDialogProps> = ({
  open,
  onOpenChange,
  containerType,
  ownerOrganizationId: ownerOrganizationIdProp,
}) => {
  const t = useT();
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const isEdit = !!containerType;
  const ownerOrganizationId =
    ownerOrganizationIdProp ?? (containerType ? Number(containerType.ownerOrganizationId) : null);
  // Another company's type: shown; changed only with a write right on its owner.
  const canWrite = useCanWriteType("container_type", ownerOrganizationIdProp);
  const readOnly = !!containerType && !canWrite(containerType);
  const foreignOwner = containerType
    ? foreignOwnerName(containerType, ownerOrganizationId)
    : undefined;
  const [createContainerType, { isLoading: isCreating }] = useCreateContainerTypeMutation();
  const [updateContainerType, { isLoading: isUpdating }] = useUpdateContainerTypeMutation();
  const isSubmitting = isCreating || isUpdating;

  // Same query as the page, so this is a cache hit. Archived types count too:
  // restoring one would otherwise bring back a second type with the same name.
  const { data: containerTypesData } = useListContainerTypesQuery(
    { ownerOrganizationIds: ownerOrganizationId ? [BigInt(ownerOrganizationId)] : [] },
    { skip: !ownerOrganizationId },
  );
  // Keeping the current name is never a clash, so types that already share a
  // name stay editable (the backend applies the same rule).
  const isNameTaken = (name: string) => {
    const key = name.trim().toLowerCase();
    if (containerType && key === containerType.name.trim().toLowerCase()) return false;
    // The list spans the holding; a name is unique within the type's company only (a sister's needs another volume too).
    return (containerTypesData?.containerTypes ?? []).some(
      (type) =>
        type.id !== containerType?.id &&
        isOwnType(type, containerType?.ownerOrganizationId ?? ownerOrganizationId) &&
        type.name.trim().toLowerCase() === key,
    );
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BunkerTypeFormData>({
    defaultValues: {
      name: "",
      volume: 0,
      weight: 0,
    },
  });

  useEffect(() => {
    if (containerType) {
      reset({
        name: containerType.name,
        volume: containerType.volume,
        weight: containerType.weightKg,
      });
    } else {
      reset({
        name: "",
        volume: 0,
        weight: 0,
      });
    }
  }, [containerType, reset]);

  const onSubmit = async (data: BunkerTypeFormData) => {
    try {
      if (isEdit && containerType) {
        await updateContainerType({
          id: containerType.id,
          ownerOrganizationId: containerType.ownerOrganizationId,
          name: data.name.trim(),
          volume: data.volume,
          weightKg: data.weight,
        }).unwrap();

        toast({
          title: t(Namespace.Settings, "bunkerType.dialog.updateSuccess"),
        });
      } else {
        if (!ownerOrganizationId) {
          toast({
            title: t(Namespace.Settings, "bunkerType.dialog.error"),
            description: t(Namespace.Settings, "bunkerType.dialog.companyRequired"),
            variant: "destructive",
          });
          return;
        }

        await createContainerType({
          name: data.name.trim(),
          ownerOrganizationId: BigInt(ownerOrganizationId),
          volume: data.volume,
          weightKg: data.weight,
        }).unwrap();

        toast({
          title: t(Namespace.Settings, "bunkerType.dialog.createSuccess"),
        });
      }

      onOpenChange(false);
      reset();
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "bunkerType.dialog.error"),
        // e.g. the holding already has this name and volume: the message names it and its company
        description: describeError(error as ApiError),
        variant: "destructive",
      });
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm">
        <ModalHeader>
          <ModalTitle>
            {readOnly
              ? t(Namespace.Settings, "bunkerType.dialog.viewTitle")
              : isEdit
                ? t(Namespace.Settings, "bunkerType.dialog.editTitle")
                : t(Namespace.Settings, "bunkerType.dialog.createTitle")}
            {foreignOwner && (
              <span className="ml-2 text-sm font-normal text-text-secondary">{foreignOwner}</span>
            )}
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)} id="bunker-type-form">
            <fieldset disabled={readOnly} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t(Namespace.Settings, "bunkerType.dialog.name")} *</Label>
                <Input
                  id="name"
                  {...register("name", {
                    validate: (value) => {
                      if (!value.trim()) return "Название обязательно";
                      if (isNameTaken(value))
                        return t(Namespace.Settings, "bunkerType.dialog.nameTaken");
                      return true;
                    },
                  })}
                  placeholder={t(Namespace.Settings, "bunkerType.dialog.namePlaceholder")}
                />
                {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="volume">
                  {t(Namespace.Settings, "bunkerType.dialog.volume")} *
                </Label>
                <Input
                  id="volume"
                  type="number"
                  step="any"
                  {...register("volume", {
                    required: "Объем обязателен",
                    valueAsNumber: true,
                    validate: (value) => value > 0 || "Объем должен быть больше 0",
                  })}
                  placeholder={t(Namespace.Settings, "bunkerType.dialog.volumePlaceholder")}
                />
                {errors.volume && <p className="text-sm text-red-600">{errors.volume.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">
                  {t(Namespace.Settings, "bunkerType.dialog.weight")} *
                </Label>
                <Input
                  id="weight"
                  type="number"
                  step="any"
                  {...register("weight", {
                    required: "Вес обязателен",
                    valueAsNumber: true,
                    min: { value: 0, message: "Вес должен быть больше 0" },
                  })}
                  placeholder={t(Namespace.Settings, "bunkerType.dialog.weightPlaceholder")}
                />
                {errors.weight && <p className="text-sm text-red-600">{errors.weight.message}</p>}
              </div>
            </fieldset>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t(Namespace.Buttons, "cancel")}
          </Button>
          {!readOnly && (
            <Button form="bunker-type-form" type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t(Namespace.Buttons, isEdit ? "save" : "add")
                : isEdit
                  ? t(Namespace.Buttons, "save")
                  : t(Namespace.Buttons, "add")}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
